import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrg } from '../orgs/org-context';
import { useControlPlane } from '../../controlPlane/ControlPlaneProvider';
import { loadOrganizationApps, launchApplication, internalLaunchTarget } from '../../controlPlane/controlPlaneApi';
import LegacyAppsContent from './LegacyAppsContent';
import AppCard from './AppCard';
import FamilyCatalog from './FamilyCatalog';
import AppsHeader from './AppsHeader';
import './AppsPage.css';

function ManagedLauncher({ organizationId, organizationName }) {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: null, apps: [] });
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [launchError, setLaunchError] = useState(null);
  const active = useRef(false);
  const launchController = useRef(null);
  useLayoutEffect(() => {
    active.current = true;
    return () => { active.current = false; launchController.current?.abort(); };
  }, []);
  useEffect(() => {
    let current = true;
    const controller = new AbortController();
    loadOrganizationApps(organizationId, controller.signal).then(data => {
      if (!current || !active.current) return;
      if (data?.contractVersion !== 1 || data.organizationId !== organizationId || !Array.isArray(data.applications) ||
          !data.applications.every(app => app && typeof app.appKey === 'string' && typeof app.displayName === 'string' &&
            typeof app.description === 'string' && (app.state !== 'open' ||
              (Array.isArray(app.instances) && app.instances.every(instance => typeof instance?.applicationId === 'string' && instance.applicationId))))) {
        throw new Error('Invalid applications response');
      }
      setState({ loading: false, error: null, apps: data.applications });
    }).catch(() => {
      if (current && active.current) setState({ loading: false, error: 'Unable to load applications for this organization.', apps: [] });
    });
    return () => { current = false; controller.abort(); };
  }, [organizationId, attempt]);

  const launch = async applicationId => {
    if (launchController.current || !active.current) return;
    const controller = new AbortController();
    launchController.current = controller;
    setBusy(true); setLaunchError(null);
    try {
      const result = await launchApplication(organizationId, applicationId, controller.signal);
      if (!active.current || controller.signal.aborted) return;
      const target = internalLaunchTarget(result, organizationId, applicationId);
      navigate(target);
    } catch (_) {
      if (active.current && !controller.signal.aborted) setLaunchError('Application could not be opened. Please try again.');
    } finally {
      if (active.current) { launchController.current = null; setBusy(false); }
    }
  };

  return <main className="apps-page">
    {(state.loading || state.error) && <AppsHeader organizationName={organizationName} />}
    {state.loading && <p role="status">Loading applications…</p>}
    {state.error && <div role="alert"><p>{state.error}</p><button className="app-primary-button" onClick={() => {
      setState({ loading: true, error: null, apps: [] }); setAttempt(value => value + 1);
    }}>Retry</button></div>}
    {launchError && <p role="alert">{launchError}</p>}
    {busy && <p role="status">Opening application…</p>}
    {!state.loading && !state.error && <section aria-label="Managed applications">
      <FamilyCatalog key={attempt} apps={state.apps} organizationName={organizationName}
        renderCard={app => <AppCard app={app} busy={busy} onLaunch={launch} />} />
    </section>}
  </main>;
}

export default function AppsPage() {
  const { loading, error, enabled, retry, actorId, catalog } = useControlPlane();
  const { activeOrgId, activeOrg } = useOrg();
  if (error) return <main className="apps-page" role="alert"><p>{error}</p>
    <button className="app-primary-button" onClick={retry}>Retry</button></main>;
  if (loading || enabled === null) return <main className="apps-page" role="status">Loading applications…</main>;
  if (enabled === false) return <LegacyAppsContent catalog={catalog.applications} organizationName={activeOrg?.organization} />;
  if (!activeOrgId) return <main className="apps-page"><h1>Apps</h1><p>Select an organization to view applications.</p></main>;
  // Org changes unmount old cards/selection immediately, even when a request
  // ignores AbortSignal or the user subsequently switches back to the old org.
  return <ManagedLauncher key={`${actorId}:${activeOrgId}`} organizationId={String(activeOrgId)} organizationName={activeOrg?.organization} />;
}
